// Beat state (SPEC F7): the live projects and the active one. The projects repo is the only writer; this
// store keeps the list on screen current. The active project's id lives in settings (app store), so a
// relaunch reopens it (DESIGN_SYSTEM §5: continuity).
import { create } from 'zustand';
import { useAppStore } from '../../app/store';
import { styleEntry } from '../../data/styles';
import type { Project, StyleId } from '../../data/types';
import * as repo from '../../db/repos/projects';
import { phaseOfStep } from '../../data/phases';
import { guardActions } from '../../lib/writeGuard';
import { recordActivity } from './journalStore';

interface ProjectsState {
  /** Every live project (archived included), most recently changed first. Undefined until the first load. */
  projects?: Project[];
  load: () => Promise<void>;
  refresh: () => Promise<void>;
  /** Start a project in the current style, with a suggested kit when the library has sounds (DESIGN_SYSTEM §5). */
  create: () => Promise<Project | undefined>;
  /** Makes it the active project and the app style the project's own (provisional, DECISIONS 2026-10-08). */
  open: (id: string) => void;
  rename: (id: string, name: string) => Promise<void>;
  setBpm: (id: string, bpm: number) => Promise<void>;
  setKey: (id: string, key: { keyRoot: number; minor: boolean } | undefined) => Promise<void>;
  setCheck: (id: string, stepId: string, done: boolean) => Promise<void>;
  setNotes: (id: string, notes: string) => Promise<void>;
  setProjectStyle: (id: string, styleId: StyleId) => Promise<void>;
  duplicate: (id: string) => Promise<Project | undefined>;
  archive: (id: string, archived: boolean) => Promise<void>;
  /** Mark finished (SPEC F10): returns the previous finishedAt (`null` for none) for Undo, `undefined` when it failed. */
  markFinished: (id: string) => Promise<number | null | undefined>;
  /** Puts finishedAt back, or clears it with `null` (Not finished, and Undo). */
  setFinishedAt: (id: string, finishedAt: number | null) => Promise<number | null | undefined>;
  remove: (id: string) => Promise<void>;
  restore: (id: string) => Promise<void>;
}

const patch = (projects: Project[] | undefined, id: string, fn: (p: Project) => Project): Project[] | undefined => projects?.map((p) => (p.id === id ? fn(p) : p));

export const useProjects = create<ProjectsState>((set, get) => guardActions<ProjectsState>({
  projects: undefined,

  load: async () => {
    try {
      await repo.purgeExpiredProjects(Date.now());
    } catch (err) {
      console.warn('Could not purge old projects', err);
    }
    await get().refresh();
  },

  refresh: async () => {
    const projects = await repo.listProjects();
    set({ projects });
    // An active project that is gone (deleted elsewhere, purged) is no longer active
    const { activeProjectId } = useAppStore.getState().settings;
    if (activeProjectId && !projects.some((p) => p.id === activeProjectId && !p.archived)) useAppStore.getState().setActiveProject(undefined);
  },

  create: async () => {
    const app = useAppStore.getState();
    const styleId = app.settings.styleId;
    const bpm = styleEntry(styleId)?.bpmDefault ?? 90;
    const kitPads = await suggestedKitPads(styleId);
    const project = await repo.createProject({ styleId, bpm }, Date.now(), kitPads);
    app.setActiveProject(project.id);
    await get().refresh();
    return project;
  },

  open: (id) => {
    const app = useAppStore.getState();
    const project = get().projects?.find((p) => p.id === id);
    const changed = app.settings.activeProjectId !== id;
    app.setActiveProject(id);
    // Only a change of project moves the app style: resuming the one you are in leaves a style you picked since
    if (changed && project && project.styleId !== app.settings.styleId) app.setStyle(project.styleId);
  },

  rename: async (id, name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await repo.updateProject(id, { name: trimmed }, Date.now());
    await get().refresh();
  },

  setBpm: async (id, bpm) => {
    set((s) => ({ projects: patch(s.projects, id, (p) => ({ ...p, bpm })) }));
    recordActivity(id, { phaseId: 'spark' });
    await repo.setBpm(id, bpm, Date.now());
    await get().refresh();
  },

  setKey: async (id, key) => {
    recordActivity(id, { phaseId: 'spark' });
    await repo.setKey(id, key, Date.now());
    await get().refresh();
  },

  setCheck: async (id, stepId, done) => {
    set((s) => ({ projects: patch(s.projects, id, (p) => ({ ...p, checks: { ...p.checks, [stepId]: done } })) }));
    // A checked step is recorded with its phase; an unchecked one still counts as activity, with no step
    recordActivity(id, { phaseId: phaseOfStep(stepId)?.id, ...(done ? { stepId } : {}) });
    await repo.setCheck(id, stepId, done, Date.now());
    await get().refresh();
  },

  setNotes: async (id, notes) => {
    recordActivity(id);
    await repo.updateProject(id, { notes }, Date.now());
    await get().refresh();
  },

  setProjectStyle: async (id, styleId) => {
    await repo.updateProject(id, { styleId }, Date.now());
    await get().refresh();
  },

  duplicate: async (id) => {
    const copy = await repo.duplicateProject(id, Date.now());
    await get().refresh();
    return copy;
  },

  archive: async (id, archived) => {
    await repo.archiveProject(id, archived, Date.now());
    await get().refresh();
  },

  markFinished: async (id) => {
    const prev = await repo.markFinished(id, Date.now());
    await get().refresh();
    return prev;
  },

  setFinishedAt: async (id, finishedAt) => {
    const prev = await repo.setFinishedAt(id, finishedAt);
    await get().refresh();
    return prev;
  },

  remove: async (id) => {
    await repo.softDeleteProject(id, Date.now());
    await get().refresh();
  },

  restore: async (id) => {
    await repo.restoreProject(id);
    await get().refresh();
  },
}, ['create', 'rename', 'setBpm', 'setKey', 'setCheck', 'setNotes', 'setProjectStyle', 'duplicate', 'archive', 'markFinished', 'setFinishedAt', 'remove', 'restore'])); // a failed save toasts and leaves the list as it was

/** The new project's kit: Suggest kit's pads when the library has sounds, else undefined (no kit is made). */
async function suggestedKitPads(styleId: StyleId) {
  const [{ listSamples }, { suggestKit }, { newKitPads }] = await Promise.all([
    import('../../db/repos/samples'),
    import('../../lib/kit'),
    import('../drums/kit/kitStore'),
  ]);
  const samples = await listSamples();
  if (samples.length === 0) return undefined;
  const result = suggestKit(newKitPads().map((p) => ({ level: 1, pan: 0, tuneSemi: 0, tuneCents: 0, decay: 1, ...p })), samples, styleEntry(styleId)?.flavor ?? 'clean');
  return result.suggestions.length > 0 ? result.pads : undefined;
}

/** The active project, once the list has loaded and it still exists. */
export function useActiveProject(): Project | undefined {
  const id = useAppStore((s) => s.settings.activeProjectId);
  return useProjects((s) => (id ? s.projects?.find((p) => p.id === id && !p.archived) : undefined));
}

export const useProject = (id: string | undefined): Project | undefined => useProjects((s) => (id ? s.projects?.find((p) => p.id === id) : undefined));
